import {
  CarIcon,
  ChatCircleIcon,
  MapPinAreaIcon,
  MotorcycleIcon,
  StarIcon,
  UserCircleCheckIcon,
} from '@phosphor-icons/react'
import Link from 'next/link'
import { Fragment } from 'react'
import { FahrlehrerCh } from '@/src/@types/blinkadmin'
import Button from '@/src/components/core/Button'
import IconGradientWrapper from '@/src/components/core/icons/IconGradientWrapper'
import Picture from '@/src/components/elements/Picture'

const transmissionLabels: Record<string, string> = {
  automatic: 'Automat',
  manual: 'Geschaltet',
}

const blinkOffers = [
  { label: 'Auto', slug: 'fahrschule' },
  { label: 'Motorrad', slug: 'motorradgrundkurs' },
  { label: 'VKU', slug: 'vku' },
  { label: 'Nothelferkurse', slug: 'nothelferkurs' },
]

function formatVehicles(
  vehicles: FahrlehrerCh.TeacherProfileVehicle[]
): string {
  const grouped = vehicles
    .filter((c) => c.brand && c.model)
    .reduce<Record<string, Set<string>>>((acc, c) => {
      const fullModel = `${c.brand!} ${c.model!}`
      if (!acc[fullModel]) acc[fullModel] = new Set()
      const transmission = c.transmissionType
        ? (transmissionLabels[c.transmissionType] ?? c.transmissionType)
        : null
      if (transmission) acc[fullModel].add(transmission)
      return acc
    }, {})

  return Object.entries(grouped)
    .map(([fullModel, transmissions]) => {
      const parts = [...transmissions]
      return parts.length === 0
        ? fullModel
        : `${fullModel}, ${parts.join(' & ')}`
    })
    .join(' & ')
}

function getAverageRating(
  reviews: FahrlehrerCh.TeacherProfileReview[]
): number | null {
  const values: number[] = []
  for (const review of reviews) {
    const r = review.ratings
    for (const v of [
      r.progress,
      r.explanations,
      r.learningGoals,
      r.attention,
      r.patience,
      r.scheduling,
      r.cleanliness,
      r.punctuality,
    ]) {
      if (v !== null) values.push(v)
    }
  }
  return values.length > 0
    ? values.reduce((s, v) => s + v, 0) / values.length
    : null
}

function fmtRating(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

const StarRating = ({ rating }: { rating: number }) => (
  <div
    className="flex items-center gap-0.5"
    aria-label={`${fmtRating(rating)} von 5`}
  >
    {Array.from({ length: 5 }).map((_, i) => (
      <StarIcon
        key={i}
        size={16}
        weight="fill"
        className={i < Math.round(rating) ? 'text-yellow-500' : 'text-gray-300'}
      />
    ))}
  </div>
)

const QuoteIcon = () => (
  <svg
    width="39"
    height="30"
    viewBox="0 0 39 30"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="pointer-events-none absolute -top-8 -left-2 select-none"
  >
    <path
      opacity="0.5"
      d="M0 30V21.0867C0 18.5557 0.491596 15.9697 1.47479 13.3287C2.45798 10.6878 3.75525 8.19807 5.3666 5.8597C6.97794 3.52132 8.72584 1.56809 10.6103 0L18.312 4.58047C16.7826 7.00138 15.5263 9.53232 14.5431 12.1733C13.5872 14.8143 13.1092 17.7579 13.1092 21.0041V30H0ZM20.688 30V21.0867C20.688 18.5557 21.1796 15.9697 22.1628 13.3287C23.146 10.6878 24.4433 8.19807 26.0546 5.8597C27.666 3.52132 29.4139 1.56809 31.2983 0L39 4.58047C37.4706 7.00138 36.2143 9.53232 35.2311 12.1733C34.2752 14.8143 33.7973 17.7579 33.7973 21.0041V30H20.688Z"
      fill="#B5C1FF"
    />
  </svg>
)

type LocationGroup = FahrlehrerCh.LocationPageTeacherLocation & {
  teachers: FahrlehrerCh.LocationPageTeacher[]
}

/**
 * Determines how a location page is laid out based on how many teachers are
 * located at the page's main location:
 * - `single`: at least 3 teachers at the main location → flat list of them.
 * - `mixed`: 1-2 teachers at the main location → those first, then nearby
 *   teachers grouped by location.
 * - `nearby`: no teachers at the main location → a hint plus all teachers
 *   grouped by location.
 */
export function getLocationLayout(
  teachers: FahrlehrerCh.LocationPageTeacher[],
  locationId: number | null
) {
  const localTeachers = teachers.filter(
    (teacher) => teacher.location.id === locationId
  )
  const mode =
    localTeachers.length >= 3
      ? 'single'
      : localTeachers.length > 0
        ? 'mixed'
        : 'nearby'
  return { localTeachers, mode } as const
}

function groupByLocation(
  teachers: FahrlehrerCh.LocationPageTeacher[]
): LocationGroup[] {
  const groups = new Map<number, LocationGroup>()
  for (const teacher of teachers) {
    const existing = groups.get(teacher.location.id)
    if (existing) {
      existing.teachers.push(teacher)
    } else {
      groups.set(teacher.location.id, {
        ...teacher.location,
        teachers: [teacher],
      })
    }
  }
  return [...groups.values()].sort((a, b) => a.distanceKm - b.distanceKm)
}

const List = ({
  filteredTeachers,
  swissPlaceName,
  locationId,
  teachers,
}: {
  filteredTeachers: FahrlehrerCh.LocationPageTeacher[]
} & FahrlehrerCh.LocationPage) => {
  const { mode, localTeachers: allLocalTeachers } = getLocationLayout(
    teachers,
    locationId
  )
  const localLocation = allLocalTeachers[0]?.location

  if (mode === 'single') {
    return (
      <div className="mt-6">
        <TeacherList teachers={filteredTeachers} />
        {localLocation && <BlinkCrosslinks location={localLocation} />}
      </div>
    )
  }

  const localTeachers = filteredTeachers.filter(
    (teacher) => teacher.location.id === locationId
  )
  const nearbyGroups = groupByLocation(
    filteredTeachers.filter((teacher) => teacher.location.id !== locationId)
  )

  return (
    <div>
      {mode === 'nearby' ? (
        <NoTeachersHint placeName={swissPlaceName} />
      ) : (
        <>
          <TeacherList teachers={localTeachers} />
          {localLocation && <BlinkCrosslinks location={localLocation} />}
        </>
      )}
      {nearbyGroups.length > 0 && (
        <div>
          {mode === 'mixed' && (
            <h3 className="mt-16 mb-6 text-2xl font-black">
              Fahrlehrer:innen in der Nähe von {swissPlaceName}
            </h3>
          )}
          <div className="space-y-16">
            {nearbyGroups.map((group) => (
              <div key={group.id}>
                <LocationGroupHeader
                  name={group.name}
                  distanceKm={group.distanceKm}
                />
                <div className="mt-6">
                  <TeacherList teachers={group.teachers} />
                  <BlinkCrosslinks location={group} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const TeacherList = ({
  teachers,
}: {
  teachers: FahrlehrerCh.LocationPageTeacher[]
}) => (
  <div className="space-y-6">
    {teachers.map((teacher) => (
      <Teacher key={teacher.handle} {...teacher} />
    ))}
  </div>
)

const BlinkCrosslinks = ({
  location,
}: {
  location: FahrlehrerCh.LocationPageTeacherLocation
}) => (
  <p className="mt-4 text-sm text-gray-600">
    Fahrlehrer in {location.name} by BLINK:{' '}
    {blinkOffers.map((offer, index) => (
      <Fragment key={offer.slug}>
        {index > 0 && <span className="text-gray-300"> | </span>}
        <Link
          href={`https://blinkdrive.ch/${offer.slug}-${location.slug}`}
          target="_blank"
          className="font-bold text-blue-700"
        >
          {offer.label}
        </Link>
      </Fragment>
    ))}
  </p>
)

const LocationGroupHeader = ({
  name,
  distanceKm,
}: {
  name: string
  distanceKm: number
}) => (
  <div className="flex items-center gap-2">
    <MapPinAreaIcon
      weight="bold"
      size={24}
      className="shrink-0 text-gray-800"
    />
    <span className="text-lg font-bold">
      {name}, {Math.round(distanceKm)} km
    </span>
  </div>
)

const NoTeachersHint = ({ placeName }: { placeName: string }) => (
  <div className="relative my-6 overflow-hidden rounded-2xl bg-gray-50 p-6">
    <div className="flex flex-col gap-3 sm:max-w-150">
      <p className="text-lg font-black">
        In {placeName} sind aktuell keine Fahrlehrer:innen verfügbar.
      </p>
      <p>
        Damit du trotzdem schnell fündig wirst, hier einige Fahrlehrer:innen aus
        der Umgebung.
      </p>
    </div>
    <span className="pointer-events-none absolute top-3 right-8 hidden sm:block">
      <IconGradientWrapper from="#1a34d0" to="#1628a4">
        <MapPinAreaIcon weight="bold" className="size-38" />
      </IconGradientWrapper>
    </span>
  </div>
)

const Teacher = ({ ...teacher }: FahrlehrerCh.LocationPageTeacher) => {
  const autoCars = teacher.vehicles.filter((c) => c.type === 'car')
  const motorcycleCars = teacher.vehicles.filter((c) => c.type === 'motorcycle')

  const yearsExperience = teacher.certificationSince
    ? new Date().getFullYear() - teacher.certificationSince
    : null
  const avgRating = getAverageRating(teacher.feedbacks)
  const review = teacher.feedbacks.find((f) => f.text)
  const reviewRating = review ? getAverageRating([review]) : null

  const bookingLink = teacher.bookable
    ? `/probelektion?instructorContext=${teacher.slug}&location=${teacher.location.id}`
    : `/${teacher.handle}?location=${teacher.location.id}#booking`

  return (
    <div className="overflow-hidden rounded-xl border border-gray-100 bg-gray-50 lg:flex lg:gap-4 lg:border-0">
      <div className="lg:w-80 lg:shrink-0 lg:p-8 lg:pr-6">
        <div className="relative aspect-7/5 w-full overflow-hidden lg:rounded-lg">
          {teacher.avatar && (
            <Picture
              permalink={teacher.avatar.url}
              fill
              alt={`${teacher.name}, Fahrlehrperson in ${teacher.location.name}`}
              className="object-cover"
              sizes="(min-width: 1024px) 264px, 100vw"
            />
          )}
        </div>
        <div className="mt-6 hidden flex-col gap-3 lg:flex">
          <Button
            as={Link}
            href={`/${teacher.handle}?location=${teacher.location.id}`}
            version="blue"
            size="small"
            className="flex-1"
          >
            Profil ansehen
          </Button>
          <Button
            as={Link}
            href={bookingLink}
            version="gray-outlined"
            size="small"
            className="flex-1"
          >
            Probelektion buchen
          </Button>
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-8 px-5 pt-4 pb-6 lg:gap-10 lg:py-8 lg:pr-8 lg:pl-0">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-0.5">
              <p className="text-2xl font-black text-gray-900">
                {teacher.name}
              </p>
              {teacher.shortDescription && (
                <p className="text-gray-800">{teacher.shortDescription}</p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              {autoCars.length > 0 && (
                <div className="flex items-center gap-2 text-gray-800">
                  <CarIcon
                    size={20}
                    weight="bold"
                    className="shrink-0 text-gray-600"
                  />
                  <span>{formatVehicles(autoCars)}</span>
                </div>
              )}
              {motorcycleCars.length > 0 && (
                <div className="flex items-center gap-2 text-gray-800">
                  <MotorcycleIcon
                    size={20}
                    weight="bold"
                    className="shrink-0 text-gray-600"
                  />
                  <span>{formatVehicles(motorcycleCars)}</span>
                </div>
              )}
              {teacher.lessonLanguages.length > 0 && (
                <div className="flex items-center gap-2 text-gray-800">
                  <ChatCircleIcon
                    size={20}
                    weight="bold"
                    className="shrink-0 text-gray-600"
                  />
                  <span>{teacher.lessonLanguages.join(', ')}</span>
                </div>
              )}
              {yearsExperience !== null && (
                <div className="flex items-center gap-2 text-gray-800">
                  <UserCircleCheckIcon
                    size={20}
                    weight="bold"
                    className="shrink-0 text-gray-600"
                  />
                  <span>{yearsExperience} Jahre Erfahrung</span>
                </div>
              )}
            </div>
          </div>
          {teacher.profileTags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {teacher.profileTags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-lg bg-gray-150 px-3 py-1.5 text-sm text-gray-900"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
        {(teacher.shortSentence || review) && (
          <div className="flex flex-col gap-6">
            {teacher.shortSentence && (
              <div className="rounded-lg bg-white p-5 shadow-sm">
                <div className="relative">
                  <QuoteIcon />
                  <p className="text-gray-800">{teacher.shortSentence}</p>
                </div>
              </div>
            )}
            {review && (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    {reviewRating !== null && (
                      <StarRating rating={reviewRating} />
                    )}
                    <span className="text-sm font-bold text-gray-800">
                      {review.firstName ?? 'anonym'}
                    </span>
                  </div>
                  <p className="line-clamp-3 text-sm text-gray-800">
                    {review.text}
                  </p>
                </div>
                {avgRating !== null && (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <span className="font-bold text-gray-900">
                        {fmtRating(avgRating)}/5
                      </span>
                      <StarIcon
                        size={20}
                        weight="fill"
                        className="text-yellow-500"
                      />
                    </div>
                    <span className="text-gray-600">
                      {teacher.feedbacks.length}{' '}
                      {teacher.feedbacks.length === 1
                        ? 'Bewertung'
                        : 'Bewertungen'}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div className="flex flex-col gap-3 lg:hidden">
          <Button
            as={Link}
            href={`/${teacher.handle}?location=${teacher.location.id}`}
            version="blue"
            className="flex-1"
          >
            Profil ansehen
          </Button>
          <Button
            as={Link}
            href={bookingLink}
            version="gray-outlined"
            className="flex-1"
          >
            Probelektion buchen
          </Button>
        </div>
      </div>
    </div>
  )
}

export default List
